/* eslint-disable react/button-has-type */
import * as React from 'react';

import { Button } from '@lightspeed/flame/Button';
import { Box, FlameTheme, Flex } from '@lightspeed/flame/Core';
import { theme } from '@lightspeed/flame/Core/themes/flame';
import { IconDanger } from '@lightspeed/flame/Icon/Danger';
import { IconSync } from '@lightspeed/flame/Icon/Sync';
import { IconVerified } from '@lightspeed/flame/Icon/Verified';
import { Text } from '@lightspeed/flame/Text';
import get from 'lodash/get';
import Moment from 'moment';

import Format from 'app/src/data/Format';
import PaymentType from 'app/src/data/models/PaymentType';
import Localize from 'app/src/localize/Localize';

export interface IViewData {}

export interface ISalePayment {
  details: {
    isDebit: boolean;
    maskedDigits: string;
    status: string;
    transactionDate: string;
    transactionDateString: string;
  };
  model: {
    createTime: string;
    CCCharge?: {
      xnum: string;
    };
  };
  get(property: string): any;
}

function RemoveButton({ isLoading, onClick }: { isLoading: boolean; onClick: () => void }) {
  return (
    <Button mr="6px" loading={isLoading} onClick={onClick}>
      Remove
    </Button>
  );
}

export const PaymentListItem = ({ model, viewData }: { model: ISalePayment; viewData: any }) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isCancelLoading, setIsCancelLoading] = React.useState<boolean>(false);
  const isRefund = model.get('amount').isNegative();
  const isDeposit = model.get('PaymentType').type === PaymentType.Types.CREDIT_ACCOUNT;
  const isCCCharge = !!model?.model?.CCCharge;
  const isSavedPayment = model.get('paymentID')?.startsWith('cof_');

  const cardInformation = (payment: ISalePayment) => {
    let cardInformation = '';

    if (payment.details) {
      const cardType = payment.details.isDebit
        ? Localize.translate('Debit')
        : Localize.translate('Credit Card');

      cardInformation = `${cardType} ${
        payment.details.maskedDigits ? `(${payment.details.maskedDigits})` : ''
      }`;
    } else if (isCCCharge) {
      cardInformation = `${payment?.model?.CCCharge?.xnum}`;
    } else if (isDeposit) {
      // localize expects it this way
      cardInformation =
        Localize.translate('Deposit Refund - ') +
        Localize.translate(get(viewData, 'depositPayment.model.PaymentType.name', ''));
    } else if (isRefund && payment.get('paymentID').startsWith('pmlnk_')) {
      cardInformation = Localize.translate('Payment Link Deposit Refund');
    } else if (isSavedPayment) {
      const lastFour = payment.get('paymentID').match(/^cof_(\d+)_/);
      cardInformation = lastFour
        ? Localize.translate('Saved Card (%s)').replace('%s', lastFour[1])
        : Localize.translate('Saved Payment Method');
    } else if (isRefund) {
      cardInformation = Localize.translate('Refund');
    }

    return cardInformation;
  };

  const transactionDate = (payment: ISalePayment) => {
    let transactionDate;

    if (isCCCharge) {
      const dateTime = payment.model.createTime.split('T');
      transactionDate = `${dateTime[0]} ${dateTime[1]}`;
    } else if (isDeposit) {
      transactionDate = Date.now();
    }

    const userLocale = window.merchantos.locale.lang || 'en-US';

    return Moment(transactionDate || payment?.details?.transactionDateString)
      .locale(userLocale)
      .format('lll')
      .toString();
  };

  async function resolvePayment() {
    // An event gets called from the list view
    setIsLoading(true);
    try {
      await viewData.controller.onPaymentResolveButtonClicked({ model });
    } catch (err) {
      setIsLoading(false);
    }
  }

  async function cancelPayment() {
    // An event gets called from the list view
    setIsCancelLoading(true);
    try {
      await viewData.controller.onPaymentCancelButtonClicked({ model });
    } catch (err) {
      setIsCancelLoading(false);
    }
  }

  async function removePayment() {
    setIsLoading(true);
    try {
      await viewData.controller.onPaymentRemoveButtonClicked({ model });
    } catch (err) {
      setIsLoading(false);
    }
  }

  let payment;

  if (model.get('PaymentType').type === PaymentType.Types.GIFT_CARD) {
    payment = (
      <Box p={2} style={{ background: '#FFFFFF' }}>
        <Flex justifyContent="space-between" justifyItems="center">
          <Flex justifyItems="center" alignItems="center">
            <IconVerified color={theme.colors['green-800']} />

            <Text fontSize="14px" fontWeight="bold" marginLeft="12px">
              {Localize.translate('Gift Card')}
            </Text>
          </Flex>

          <Text fontSize="14px" fontWeight="bold">
            {Format.formatMoney(model.get('amount'), 0)}
          </Text>
        </Flex>

        <Flex ml="28px" mt="15px">
          <Button mr="6px" loading={isLoading} onClick={removePayment}>
            Remove
          </Button>
        </Flex>
      </Box>
    );
  } else if (
    model.details &&
    model.get('PaymentType').type === PaymentType.Types.CREDIT_CARD &&
    !isRefund &&
    get(model, 'details.status', '') === 'pending'
  ) {
    payment = (
      <Box p={2} style={{ background: '#FFFFFF' }}>
        <Flex justifyContent="space-between" justifyItems="center">
          <Flex justifyItems="center" alignItems="center">
            <IconDanger color={theme.colors['maple-500']} />

            <Text fontSize="14px" fontWeight="bold" ml="12px">
              {Localize.translate('Pending payment')}
            </Text>
          </Flex>

          <Text fontSize="14px" fontWeight="bold">
            {Format.formatMoney(model.get('amount'), 0)}
          </Text>
        </Flex>

        <Flex>
          <Text fontSize="12px" ml="28px" mt="6px" color={theme.colors.textDimmed}>
            {Localize.translate(
              'This sale has a pending payment that needs to be resolved. Select Resolve before you edit or finish the sale.'
            )}
          </Text>
        </Flex>

        <Flex ml="28px" mt="15px">
          <Box flexGrow={2}>
            <Button
              block
              variant="secondary"
              fill
              loading={isLoading}
              disabled={isCancelLoading}
              onClick={resolvePayment}
            >
              <IconSync />
              Resolve
            </Button>
          </Box>
          <Box>
            <Button ml="6px" loading={isCancelLoading} disabled={isLoading} onClick={cancelPayment}>
              Cancel
            </Button>
          </Box>
        </Flex>
      </Box>
    );
  } else if (get(model, 'details.status', '') !== 'pending') {
    payment = (
      <Box p={2} style={{ background: '#FFFFFF' }}>
        <Flex justifyContent="space-between" justifyItems="center">
          <Flex justifyItems="center" alignItems="center">
            <IconVerified color={theme.colors['green-800']} />

            <Text fontSize="14px" fontWeight="bold" marginLeft="12px">
              {cardInformation(model)}
            </Text>
          </Flex>

          <Text fontSize="14px" fontWeight="bold">
            {Format.formatMoney(model.get('amount'), 0)}
          </Text>
        </Flex>

        <Flex>
          <Text fontSize="12px" marginLeft="28px" marginTop="6px" color={theme.colors.textDimmed}>
            <var>{transactionDate(model)}</var>
          </Text>
        </Flex>

        {!isRefund && (
          <Flex ml="28px" mt="15px">
            <RemoveButton isLoading={isLoading} onClick={removePayment} />
          </Flex>
        )}
      </Box>
    );
  }
  ``;

  return <FlameTheme>{payment}</FlameTheme>;
};
